import React, { useState, useEffect } from 'react';
import { obrasAPI, clientesAPI, presupuestosAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, Search, MoreVertical, Pencil, Trash2, Building2,
  Play, Pause, CheckCircle, XCircle
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor, translateEstado, getTodayISO } from '../lib/utils';

const ObrasPage = () => {
  const [obras, setObras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedObra, setSelectedObra] = useState(null);
  const [formData, setFormData] = useState({
    cliente_id: '',
    presupuesto_id: '',
    titulo: '',
    direccion: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin_prevista: '',
    estado: 'pendiente',
    presupuesto_previsto: 0,
    notas: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [obrasRes, clientesRes, presupuestosRes] = await Promise.all([
        obrasAPI.list(),
        clientesAPI.list(),
        presupuestosAPI.list(),
      ]);
      setObras(obrasRes.data);
      setClientes(clientesRes.data);
      setPresupuestos(presupuestosRes.data.filter(p => p.estado === 'aceptado'));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedObra) {
        await obrasAPI.update(selectedObra.id, formData);
        toast.success('Obra actualizada');
      } else {
        await obrasAPI.create(formData);
        toast.success('Obra creada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar obra');
    }
  };

  const handleEdit = (obra) => {
    setSelectedObra(obra);
    setFormData({
      cliente_id: obra.cliente_id || '',
      presupuesto_id: obra.presupuesto_id || '',
      titulo: obra.titulo || '',
      direccion: obra.direccion || '',
      descripcion: obra.descripcion || '',
      fecha_inicio: obra.fecha_inicio || '',
      fecha_fin_prevista: obra.fecha_fin_prevista || '',
      estado: obra.estado || 'pendiente',
      presupuesto_previsto: obra.presupuesto_previsto || 0,
      notas: obra.notas || '',
    });
    setDialogOpen(true);
  };

  const handleChangeEstado = async (obra, nuevoEstado) => {
    try {
      await obrasAPI.updateEstado(obra.id, nuevoEstado);
      toast.success(`Estado cambiado a ${translateEstado(nuevoEstado)}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async () => {
    try {
      await obrasAPI.delete(selectedObra.id);
      toast.success('Obra eliminada');
      setDeleteDialogOpen(false);
      setSelectedObra(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar obra');
    }
  };

  const resetForm = () => {
    setSelectedObra(null);
    setFormData({
      cliente_id: '',
      presupuesto_id: '',
      titulo: '',
      direccion: '',
      descripcion: '',
      fecha_inicio: getTodayISO(),
      fecha_fin_prevista: '',
      estado: 'pendiente',
      presupuesto_previsto: 0,
      notas: '',
    });
  };

  const getClienteName = (clienteId) => {
    return clientes.find((c) => c.id === clienteId)?.nombre || 'Desconocido';
  };

  const filteredObras = obras.filter(
    (o) =>
      o.titulo?.toLowerCase().includes(search.toLowerCase()) ||
      o.numero?.toLowerCase().includes(search.toLowerCase()) ||
      o.direccion?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="obras-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Obras</h1>
          <p className="text-slate-500 text-sm mt-1">{obras.length} obras registradas</p>
        </div>
        <div className="lg:hidden flex-1">
          <p className="text-xs text-slate-500">{obras.length} obras</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800 h-10 lg:h-9 active:scale-95 transition-transform"
          data-testid="new-obra-btn"
        >
          <Plus className="w-4 h-4 mr-1 lg:mr-2" />
          <span className="hidden sm:inline">Nueva Obra</span>
          <span className="sm:hidden">Nueva</span>
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar obras..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 lg:h-10 lg:max-w-md text-base lg:text-sm"
          data-testid="search-obras-input"
        />
      </div>

      {/* Content */}
      {filteredObras.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{search ? 'No se encontraron obras' : 'No hay obras'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="lg:hidden space-y-3">
            {filteredObras.map((obra) => (
              <Card 
                key={obra.id} 
                className="border-slate-200 active:scale-[0.99] transition-transform"
                data-testid={`obra-card-${obra.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded ${getStatusColor(obra.estado)}`}>
                          {translateEstado(obra.estado)}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">{obra.numero}</span>
                      </div>
                      <p className="font-semibold text-slate-900 truncate">{obra.titulo}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{getClienteName(obra.cliente_id)}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" data-testid={`obra-actions-${obra.id}`}>
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(obra)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        {obra.estado === 'pendiente' && (
                          <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'en_curso')}>
                            <Play className="w-4 h-4 mr-2" />
                            Iniciar
                          </DropdownMenuItem>
                        )}
                        {obra.estado === 'en_curso' && (
                          <>
                            <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'pausada')}>
                              <Pause className="w-4 h-4 mr-2" />
                              Pausar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'finalizada')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Finalizar
                            </DropdownMenuItem>
                          </>
                        )}
                        {obra.estado === 'pausada' && (
                          <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'en_curso')}>
                            <Play className="w-4 h-4 mr-2" />
                            Reanudar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => { setSelectedObra(obra); setDeleteDialogOpen(true); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">Presupuesto</p>
                      <p className="text-sm font-semibold text-slate-900 font-mono">{formatCurrency(obra.presupuesto_previsto)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">Inicio</p>
                      <p className="text-sm text-slate-700">{formatDate(obra.fecha_inicio) || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <Card className="border-slate-200 hidden lg:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Número</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Título</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Cliente</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Estado</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Presupuesto</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Inicio</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredObras.map((obra) => (
                      <tr key={obra.id} className="hover:bg-slate-50" data-testid={`obra-row-${obra.id}`}>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-slate-600">{obra.numero}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{obra.titulo}</p>
                            <p className="text-xs text-slate-500 truncate max-w-xs">{obra.direccion}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {getClienteName(obra.cliente_id)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getStatusColor(obra.estado)}`}>
                            {translateEstado(obra.estado)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {formatCurrency(obra.presupuesto_previsto)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(obra.fecha_inicio)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`obra-actions-desktop-${obra.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(obra)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              {obra.estado === 'pendiente' && (
                                <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'en_curso')}>
                                  <Play className="w-4 h-4 mr-2" />
                                  Iniciar
                                </DropdownMenuItem>
                              )}
                              {obra.estado === 'en_curso' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'pausada')}>
                                    <Pause className="w-4 h-4 mr-2" />
                                    Pausar
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'finalizada')}>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Finalizar
                                  </DropdownMenuItem>
                                </>
                              )}
                              {obra.estado === 'pausada' && (
                                <DropdownMenuItem onClick={() => handleChangeEstado(obra, 'en_curso')}>
                                  <Play className="w-4 h-4 mr-2" />
                                  Reanudar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => { setSelectedObra(obra); setDeleteDialogOpen(true); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="obra-dialog">
          <DialogHeader>
            <DialogTitle>{selectedObra ? 'Editar Obra' : 'Nueva Obra'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                  required
                >
                  <SelectTrigger data-testid="obra-cliente-select" className="h-12 lg:h-10">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Presupuesto (opcional)</Label>
                <Select
                  value={formData.presupuesto_id || "none"}
                  onValueChange={(value) => {
                    const actualValue = value === "none" ? "" : value;
                    const pres = presupuestos.find(p => p.id === actualValue);
                    setFormData({ 
                      ...formData, 
                      presupuesto_id: actualValue,
                      presupuesto_previsto: pres?.total || formData.presupuesto_previsto
                    });
                  }}
                >
                  <SelectTrigger data-testid="obra-presupuesto-select">
                    <SelectValue placeholder="Vincular presupuesto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin presupuesto</SelectItem>
                    {presupuestos.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.numero} - {p.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ej: Reforma integral piso Eixample"
                required
                data-testid="obra-titulo-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección *</Label>
              <Input
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Calle, número, ciudad"
                required
                data-testid="obra-direccion-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fecha Inicio</Label>
                <Input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  data-testid="obra-inicio-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha Fin Prevista</Label>
                <Input
                  type="date"
                  value={formData.fecha_fin_prevista}
                  onChange={(e) => setFormData({ ...formData, fecha_fin_prevista: e.target.value })}
                  data-testid="obra-fin-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Presupuesto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.presupuesto_previsto}
                  onChange={(e) => setFormData({ ...formData, presupuesto_previsto: parseFloat(e.target.value) || 0 })}
                  data-testid="obra-presupuesto-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Detalles de la obra..."
                rows={2}
                data-testid="obra-descripcion-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas internas..."
                rows={2}
                data-testid="obra-notas-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-obra-btn">
                {selectedObra ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar obra?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la obra "{selectedObra?.numero}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-obra-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ObrasPage;
